import { api } from '@goaltree/backend/convex/_generated/api'
import { coachDisplayName, coachNameOverride } from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useQuery } from 'convex/react'
import { Volume2, VolumeX, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { CATEGORY_COLORS } from '@/lib/category-color'
import { handleUsageLimit } from '@/lib/usage-limit'

export type VisualiseSlide = {
  id: string
  title: string
  category: string
  pct: number
  affirmation?: string
  imageUrl?: string | null
  videoUrl?: string | null
}

const SLIDE_MS = 8000
const HOLD_MS = 220

function SlideMedia({ slide, zoom }: { slide: VisualiseSlide; zoom: boolean }) {
  const color = CATEGORY_COLORS[slide.category.toLowerCase()] ?? '#6b7280'
  if (slide.videoUrl)
    return (
      <video
        src={slide.videoUrl}
        poster={slide.imageUrl ?? undefined}
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />
    )
  if (slide.imageUrl)
    return (
      <motion.img
        src={slide.imageUrl}
        alt=""
        draggable={false}
        initial={{ scale: 1 }}
        animate={{ scale: zoom ? 1.08 : 1 }}
        transition={{ duration: SLIDE_MS / 1000, ease: 'linear' }}
        className="absolute inset-0 h-full w-full object-cover"
      />
    )
  return (
    <div
      className="absolute inset-0"
      style={{
        background: `radial-gradient(circle at 30% 20%, ${color}99, transparent 45%), linear-gradient(160deg, #141214, ${color}cc 60%, #0b0a0b)`,
      }}
    />
  )
}

// Story-style full-screen slideshow of the board: ~8s per goal, tap the left or
// right half to step, hold to pause, Esc / × to close, arrow keys on desktop.
// 9:16 centred over a blurred copy of the visual from sm up; edge to edge below.
export function VisualiseOverlay({
  slides,
  onClose,
}: {
  slides: VisualiseSlide[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const { isPro } = useIsPro()
  const speak = useAction(api.voice.speak)
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { coachVoice?: string; coachName?: string; coachStyle?: string }
    | undefined
  const [idx, setIdx] = useState(0)
  const [progress, setProgress] = useState(0)
  const [paused, setPaused] = useState(false)
  const [audio, setAudio] = useState<'off' | 'coach'>('off')
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const idxRef = useRef(0)
  const elapsed = useRef(0)
  const pausedRef = useRef(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const held = useRef(false)
  const clips = useRef(new Map<string, { audio: string; contentType: string }>())
  const audioEl = useRef<HTMLAudioElement | null>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const count = slides.length

  function goTo(n: number) {
    if (n >= count) {
      closeRef.current()
      return
    }
    const next = Math.max(0, n)
    idxRef.current = next
    elapsed.current = 0
    setIdx(next)
    setProgress(0)
  }
  const goToRef = useRef(goTo)
  goToRef.current = goTo

  function setPause(on: boolean) {
    pausedRef.current = on
    setPaused(on)
    if (on) audioEl.current?.pause()
    else audioEl.current?.play().catch(() => {})
  }

  useEffect(() => {
    let raf = 0
    let last = performance.now()
    const tick = (now: number) => {
      const dt = now - last
      last = now
      if (!pausedRef.current) {
        elapsed.current += dt
        if (elapsed.current >= SLIDE_MS) goToRef.current(idxRef.current + 1)
        else setProgress(elapsed.current / SLIDE_MS)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeRef.current()
      else if (e.key === 'ArrowRight') goToRef.current(idxRef.current + 1)
      else if (e.key === 'ArrowLeft') goToRef.current(idxRef.current - 1)
      else if (e.key === ' ') {
        e.preventDefault()
        setPause(!pausedRef.current)
      } else return
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  const slide = slides[idx]

  useEffect(() => {
    audioEl.current?.pause()
    audioEl.current = null
    if (audio !== 'coach' || !slide) return
    let live = true
    void (async () => {
      try {
        let clip = clips.current.get(slide.id)
        if (!clip) {
          clip = await speak({
            text: slide.affirmation || slide.title,
            voice: prefs?.coachVoice,
            style: prefs?.coachStyle,
          })
          clips.current.set(slide.id, clip)
        }
        if (!live) return
        const el = new Audio(`data:${clip.contentType};base64,${clip.audio}`)
        audioEl.current = el
        if (!pausedRef.current) await el.play()
      } catch (err) {
        if (!live) return
        setAudio('off')
        if (!handleUsageLimit(err)) toast.error(t('common.error'))
      }
    })()
    return () => {
      live = false
      audioEl.current?.pause()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, audio])

  if (!slide) return null
  const coachName = coachDisplayName(
    prefs?.coachVoice,
    coachNameOverride(prefs),
    t('coach.customVoice'),
  )

  function toggleAudio() {
    if (audio === 'coach') setAudio('off')
    else if (!isPro) setUpgradeOpen(true)
    else setAudio('coach')
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('goalsBoard.visualise')}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black text-white"
    >
      {/* Letterbox backdrop: the same visual, blurred, behind the 9:16 frame. */}
      <div className="absolute inset-0 hidden scale-110 opacity-50 blur-2xl sm:block">
        {(slide.imageUrl || !slide.videoUrl) && <SlideMedia slide={slide} zoom={false} />}
      </div>
      <div
        className="relative h-full w-full touch-none select-none overflow-hidden sm:aspect-[9/16] sm:h-[min(92vh,100%)] sm:w-auto sm:rounded-2xl sm:shadow-2xl"
        onPointerDown={() => {
          held.current = false
          holdTimer.current = setTimeout(() => {
            held.current = true
            setPause(true)
          }, HOLD_MS)
        }}
        onPointerUp={(e) => {
          if (holdTimer.current) clearTimeout(holdTimer.current)
          if (held.current) {
            held.current = false
            setPause(false)
            return
          }
          const rect = e.currentTarget.getBoundingClientRect()
          goTo(e.clientX - rect.left < rect.width / 2 ? idx - 1 : idx + 1)
        }}
        onPointerCancel={() => {
          if (holdTimer.current) clearTimeout(holdTimer.current)
          if (held.current) setPause(false)
          held.current = false
        }}
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={slide.id}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SlideMedia slide={slide} zoom={!reduceMotion} />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,.4),transparent_28%,transparent_50%,rgba(0,0,0,.88))]" />
            <div className="absolute inset-x-5 bottom-20 flex flex-col gap-2 sm:inset-x-6">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/70">
                {t(`goals.cat.${slide.category.toLowerCase()}`, { defaultValue: slide.category })} ·{' '}
                {slide.pct}%
              </p>
              <h2 className="text-2xl font-black leading-tight text-balance">{slide.title}</h2>
              {slide.affirmation && (
                <p className="text-base italic leading-snug text-white/90">“{slide.affirmation}”</p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="absolute inset-x-3 top-[max(env(safe-area-inset-top),12px)] flex gap-1">
          {slides.map((s, i) => (
            <span key={s.id} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full bg-white"
                style={{ width: `${i < idx ? 100 : i === idx ? progress * 100 : 0}%` }}
              />
            </span>
          ))}
        </div>
        <div className="absolute inset-x-3 top-[calc(max(env(safe-area-inset-top),12px)+12px)] flex items-center justify-between">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={toggleAudio}
            aria-pressed={audio === 'coach'}
            className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full bg-black/35 px-3 text-xs font-semibold backdrop-blur transition-colors hover:bg-black/55"
          >
            {audio === 'coach' ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4 opacity-80" />
            )}
            {audio === 'coach'
              ? t('goalsBoard.coachReads', { name: coachName })
              : t('goalsBoard.soundOff')}
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-black/35 backdrop-blur transition-colors hover:bg-black/55"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p
          className={cn(
            'absolute inset-x-0 bottom-[max(env(safe-area-inset-bottom),24px)] text-center text-xs text-white/70 transition-opacity',
            paused && 'opacity-0',
          )}
        >
          {t('goalsBoard.holdToPause')}
        </p>
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="voice" />
    </div>,
    document.body,
  )
}
